import { useMutation, gql, useLazyQuery } from '@apollo/client';
import { useQueryParam, StringParam, ArrayParam } from 'use-query-params';
import { sanitizeUrl } from '@braintree/sanitize-url';
import {
  useGlobalState,
  ProductInCart,
  User,
  CartSummary,
  useDispatch,
} from '../components/GlobalState';
import { SITE_URL } from '../settings';
import shopifyClient from '../apollo/shopifyClient';

interface ShopifyCheckoutData {
  checkoutCreate?: {
    checkout?: {
      id: string;
      webUrl: string;
    };
  };
}

const enum CheckoutType {
  USER = 'user',
  GUEST = 'guest',
}

export const useCartParams = () => {
  const [{ cartSummary, products }, dispatch] = useGlobalState();
  const [items] = useQueryParam('item', ArrayParam);
  const [promo] = useQueryParam('promo', StringParam);
  const [zip] = useQueryParam('zip', StringParam);

  const handleCartParams = () => {
    const cartItems = items?.reduce((output: ProductInCart[], item) => {
      const urlId = item?.split('-')?.[0];
      const urlQuantity = item?.split('-')?.[1] ?? '1';
      const product = products?.find(({ sku }) => urlId === sku);
      if (product) {
        output.push({
          ...product,
          quantity: parseInt(urlQuantity, 10),
        });
      }
      return output;
    }, []);

    cartItems?.forEach((product) => {
      dispatch({
        type: 'CART_ADD',
        product,
      });
    });

    dispatch({
      type: 'CART_UPDATE_SUMMARY',
      cartSummary: {
        promo: promo ?? cartSummary?.promo,
        zip: zip ?? cartSummary?.zip,
      },
    });

    return { cartItems, cartSummary };
  };

  return [handleCartParams];
};

export const transformCart = (cart: ProductInCart[]) =>
  cart.map((item) => ({
    variantId: item.id,
    quantity: item.quantity,
  }));

export const useAddCart = <D>() => {
  const CART_ADD_MUTATION = gql`
    mutation CreateCheckout($input: CheckoutCreateInput!) {
      checkoutCreate(input: $input) {
        checkout {
          id
          webUrl
        }
      }
    }
  `;
  const result = useMutation<D>(CART_ADD_MUTATION, {
    client: shopifyClient,
  });
  return result;
};

export const useGetCheckout = () => {
  const CHECKOUT_GET_QUERY = gql`
    query GetCheckout($id: ID!) {
      node(id: $id) {
        ... on Checkout {
          createdAt
          completedAt
          orderStatusUrl
          webUrl
        }
      }
    }
  `;
  const result = useLazyQuery<{
    node: {
      createdAt: string;
      completedAt: string;
      orderStatusUrl: string;
      webUrl: string;
    };
  }>(CHECKOUT_GET_QUERY, {
    client: shopifyClient,
  });
  return result;
};

export const useGetProduct = <D>() => {
  const GET_PRODUCT_QUERY = gql`
    query DynamicProduct($id: ID) {
      product(id: $id) {
        variants(first: 100) {
          edges {
            node {
              id
              price
              quantityAvailable
            }
          }
        }
      }
    }
  `;
  return useLazyQuery<D>(GET_PRODUCT_QUERY, {
    client: shopifyClient,
  });
};

const useUpdateShippingAddress = <D>() => {
  const UPDATE_CHECKOUT_SHIPPING_ADDRESS = gql`
    mutation checkoutShippingAddressUpdateV2(
      $shippingAddress: MailingAddressInput!
      $checkoutId: ID!
    ) {
      checkoutShippingAddressUpdateV2(
        shippingAddress: $shippingAddress
        checkoutId: $checkoutId
      ) {
        checkoutUserErrors {
          code
          field
          message
        }
        checkout {
          id
          shippingAddress {
            firstName
            lastName
            address1
            province
            country
            zip
          }
        }
      }
    }
  `;
  const result = useMutation<D>(UPDATE_CHECKOUT_SHIPPING_ADDRESS, {
    client: shopifyClient,
  });
  return result;
};

const useUpdateDiscount = <D>() => {
  const UPDATE_CHECKOUT_DISCOUNT = gql`
    mutation checkoutDiscountCodeApplyV2(
      $discountCode: String!
      $checkoutId: ID!
    ) {
      checkoutDiscountCodeApplyV2(
        discountCode: $discountCode
        checkoutId: $checkoutId
      ) {
        checkoutUserErrors {
          code
          field
          message
        }
        checkout {
          id
        }
      }
    }
  `;
  const result = useMutation<D>(UPDATE_CHECKOUT_DISCOUNT, {
    client: shopifyClient,
  });
  return result;
};

export const useHandleCheckout = () => {
  const dispatch = useDispatch();
  const [addToCart] = useAddCart<ShopifyCheckoutData>();
  const [updateShippingAddress] =
    useUpdateShippingAddress<ShopifyCheckoutData>();
  const [updateDiscountCode] = useUpdateDiscount<ShopifyCheckoutData>();
  const handleCart = async (
    cart: ProductInCart[],
    cartSummary: CartSummary,
    user?: User,
  ) =>
    addToCart({
      variables: {
        input: {
          lineItems: transformCart(cart),
          email: user?.profile?.emails?.[0]?.value,
          allowPartialAddresses: true,
          customAttributes: [
            ...[{ key: 'siteUrl', value: SITE_URL }],
            ...(user?.id
              ? [
                  {
                    key: 'checkoutType',
                    value: CheckoutType.USER,
                  },
                  {
                    key: 'hpid',
                    value: user?.id,
                  },
                ]
              : [
                  {
                    key: 'checkoutType',
                    value: CheckoutType.GUEST,
                  },
                ]),
          ],
        },
      },
    }).then(async ({ data }) => {
      const checkoutUrl = data?.checkoutCreate?.checkout?.webUrl;
      const checkoutId = data?.checkoutCreate?.checkout?.id;

      if (checkoutId) {
        dispatch({
          type: 'CHECKOUT_SET_ID',
          checkoutId,
        });
      }

      if (
        user &&
        user?.profile?.addresses &&
        user?.profile?.addresses.length > 0 &&
        checkoutId
      ) {
        // Update shipping address with data from Global State. When a
        // required attribute is not set, it skips the whole address.
        await updateShippingAddress({
          variables: {
            shippingAddress: {
              firstName: user.profile.name?.givenName, // optional
              lastName: user.profile.name?.familyName, // optional
              address1: user.profile.addresses[0]?.address1, // optional
              zip: user.profile.addresses[0]?.zip, // optional
              city: user.profile.addresses[0]?.city, // required
              province: user.profile.addresses[0]?.province, // required
              country: user.profile.addresses[0]?.countryCodeV2, // required
            },
            checkoutId,
          },
        });
      }

      // Update Dicount Code
      if (cartSummary.promo.length > 0 && checkoutId) {
        await updateDiscountCode({
          variables: {
            discountCode: cartSummary.promo,
            checkoutId,
          },
        });
      }

      if (checkoutUrl) {
        window.location.href = sanitizeUrl(checkoutUrl);
      }
    });

  return [handleCart];
};

export default {
  transformCart,
  useAddCart,
  useGetProduct,
  useHandleCheckout,
  useCartParams,
};
